import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RequestStatus } from '../../shared/enums/request-status';
import { RequestState } from '../../shared/types/request-state';
import {
  addPhoneNumbersToCartRequest,
  assignPhoneNumberRequest,
  bulkAssignPhoneNumberRequest,
  bulkSetPhoneNumberLabelRequest,
  bulkUpdatePhoneNumberSettingsRequest,
  createTwillioVoiceTokenRequest,
  deletePhoneNumberRequest,
  revokePhoneNumberReleaseRequest,
  endInitiatedCallRequest,
  getContactsListForManualCallDialingRequest,
  getDialerPhoneNumberListRequest,
  getCountriesOptionsForNumbersSearchRequest,
  getCountryCodeOptionsListForFiltersRequest,
  getPhoneNumberDetailsRequest,
  getPhoneNumberLabelsRequest,
  getPhoneNumbersCartRequest,
  getMyPhoneNumberListAction,
  getRegionOptionsListForFiltersRequest,
  getUsersListRequest,
  initiateCallRequest,
  pickupCallRequest,
  purchasePhoneNumbersCartRequest,
  removePhoneNumberFromCartRequest,
  searchPhoneNumbersRequest,
  setPhoneNumberLabelRequest,
  setPrimaryPhoneNumberRequest,
  updatePhoneNumberSettingsRequest,
  getVoicemailTemplateMinimalListRequest,
  dropVoicemailTemplateRequest,
  callRecordingRequest,
  listAllCallLogsRequest,
  listIncomingVoicemailsRequest,
  listMissedCallsRequest,
  getNetworkDiagnosticTokenRequest,
  deleteCallLogRequest,
  markUnseenMissedCallCountRequest,
  markCallAsSeenRequest,
  getSignedUrlForCallRecordingRequest,
  getCallOutcomeListRequest,
  getVoicemailTemplateListRequest,
  getSignedUrlForVoicemailTemplateRequest,
  createVoicemailTemplateRequest,
  updateVoicemailTemplateRequest,
  deleteVoicemailTemplateRequest,
  getPreSignedUrlRequest,
  callLogsListCountsRequest,
  assignCallOutcomeRequest,
  getReportTeamListRequest,
  exportCallReportsRequest,
  exportCallLogsDetailedRequest,
  getReportIndividualListRequest,
  getReportsStatsRequest,
  getCallLogsProspectDrawerHeaderRequest,
  getCallLogsProspectProfileSectionRequest,
  getPhoneCallMentionableUsersRequest,
  createCallLogsNoteRequest,
  updateCallLogsNoteRequest,
  attachProspectToCallRequest,
} from './extra-actions';
import {
  capitalizeFirst,
  formatPhoneNumber,
  formatPrice,
  formatRegion,
  transformCartItemsFromAPI,
} from './helpers/utils/get-formated-numbers';
import {
  CallLogsListCountsResponse,
  CallsResponse,
  CountryOptionForNumbersSearch,
  GetPreSignedUrlResponse,
  GetSignedUrlForVoicemailTemplateResponse,
  ReportIndividualListResponse,
  ReportsStatsResponse,
  ReportTeamListResponse,
  VoicemailTemplateListResponse,
  VoicemailTemplateMinimalListResponse,
  createCallLogsNotePayload,
  updateCallLogsNotePayload,
} from './types';
import {
  CartUniqueConfiguration,
  TeamMembersList,
  GetPhoneNumberSettingsResponse,
  PhoneNumberCartItem,
  PhoneNumberCartItemFromAPI,
  PhoneNumberListResponse,
  PhoneNumberSearchResult,
} from './types/phone-numbers';
import {
  CallLog,
  CallLogsListResponse,
  CallLogsResponse,
  DetailedCallLog,
  GetSignedUrlForCallRecordingResponse,
} from './types/call-logs';
import { DialerPhoneNumberItem } from './types/manual-call-dialing';
import {
  CallManagementState,
  ProspectOption,
} from '../../shared/components/dialer/types';
import { CallLogsFiltersState } from './components/call-logs/components/call-logs-filters/type';
import { ResponseSuccess } from '../../shared/types/response';
import { flattenTeamMembers } from './helpers/helper';

export interface DialerControlsState {
  microphone: boolean;
  hold: boolean;
  record: boolean;
  voicemail: boolean;
  keypad: boolean;
}

export type PhoneNumberFilters = {
  countryCodes: string[];
  regions: RegionOptionForFilters[];
  teamMemberIds: number[];
  purchaseDate: Date[] | null;
};

export interface RegionOptionForFilters {
  region: string;
  locality: string;
  isoCountry: string;
}

interface DialerState {
  // ** Dialer Request States **
  getCountriesOptionsForNumbersSearchRequest: RequestState;
  searchPhoneNumbersRequest: RequestState;
  addPhoneNumbersToCartRequest: {
    [phoneNumber: string]: RequestState;
  };
  removePhoneNumberFromCartRequest: {
    [phoneNumber: string]: RequestState;
  };
  getPhoneNumbersCartRequest: RequestState;
  purchasePhoneNumbersCartRequest: RequestState;
  purchasePhoneNumbersCartResponse: {
    paddleTransactionId: string;
    dialerCartId?: number;
  } | null;
  getContactsListForManualCallDialingRequest: RequestState;
  createTwillioVoiceTokenRequest: RequestState;
  initiateCallRequest: RequestState;
  endInitiatedCallRequest: RequestState;
  pickupCallRequest: RequestState;
  getMyPhoneNumberListRequest: RequestState;
  getPhoneNumberLabelsRequest: RequestState;
  getPhoneNumberDetailsRequest: RequestState;
  assignPhoneNumberRequest: RequestState;
  bulkAssignPhoneNumberRequest: RequestState;
  setPhoneNumberLabelRequest: RequestState;
  bulkSetPhoneNumberLabelRequest: RequestState;
  bulkUpdatePhoneNumberSettingsRequest: RequestState;
  updatePhoneNumberSettingsRequest: {
    [code: string]: RequestState;
  };
  setPrimaryPhoneNumberRequest: RequestState;
  deletePhoneNumberRequest: RequestState;
  revokePhoneNumberReleaseRequest: RequestState;
  // getCallLogsListRequest: RequestState;
  getDialerPhoneNumberListRequest: RequestState;
  getVoicemailTemplateMinimalListRequest: RequestState;
  dropVoicemailTemplateRequest: RequestState;
  callRecordingRequest: RequestState;
  getNetworkDiagnosticTokenRequest: RequestState;

  // ** Dialer Value States **
  dialerPhoneNumberList: DialerPhoneNumberItem[];
  phoneNumbersInCart: PhoneNumberCartItem[];
  hasErrorsInContactDetailsForm: boolean;
  countriesOptionsForNumbersSearch: CountryOptionForNumbersSearch[];
  phoneNumbersList: PhoneNumberSearchResult[];
  cartUniqueConfiguration: CartUniqueConfiguration | null;
  isRegionChangedInLastSearch: boolean;
  lastSearchRegion: string | null;
  twillioVoiceToken: string | null;
  initiatedCallId: string | null;
  /** When true, Firebase connection stays closed even if initiatedCallId is set again (e.g. from race condition) */
  firebaseConnectionDisabledByUser: boolean;
  myPhoneNumbersList: PhoneNumberListResponse | null;
  countryCodeOptionsListForFilters: string[] | null;
  regionOptionsListForFilters: RegionOptionForFilters[];
  myPhoneNumbersListFilters: PhoneNumberFilters;
  teamMembersList: TeamMembersList[];
  phoneNumberLabels: string[];
  activeNumberSettings: GetPhoneNumberSettingsResponse | null;
  showDialer: boolean;
  showIncomingCall: boolean;
  callLogsList: CallLogsListResponse | null;
  prospectMinimalListResponse: any;
  voicemailTemplateMinimalList: VoicemailTemplateMinimalListResponse | null;
  dialerControlsState: DialerControlsState;
  twillioVoiceTokenSessionHashId: string | null;
  listAllCallLogsRequest: RequestState;
  listAllCallLogsResponse: CallLogsResponse;
  listMissedCallsRequest: RequestState;
  listIncomingVoicemailsRequest: RequestState;
  listMissedCallsResponse: CallLogsResponse;
  listIncomingVoicemailsResponse: CallLogsResponse;
  /** Response for the current active call logs tab (all | missed | voicemails) */
  activeCallLogsResponse: CallLogsResponse | null;
  showMicrophoneAccessWarningModal: boolean;
  networkDiagnosticIceServers: RTCIceServer[] | null;
  selectedCallbackProspect?: CallLog[];
  currentCallManagementState: CallManagementState | null;
  /** Live call network quality: 0 (no signal) – 4 (excellent). Defaults to 4 (excellent) on call connect. */
  callNetworkLevel: number;
  activeCallLogsTab: string;
  deleteCallLogRequest: RequestState;
  markUnseenMissedCallCountRequest: RequestState;
  missedCallCount: number;
  markCallAsSeenRequest: RequestState;
  getSignedUrlForCallRecordingRequest: RequestState;
  getSignedUrlForCallRecordingResponse: GetSignedUrlForCallRecordingResponse;
  getSignedUrlForCallRecordingResponses: Record<
    number,
    GetSignedUrlForVoicemailTemplateResponse
  >;
  getCallOutcomeListRequest: RequestState;
  getCallOutcomeListResponse: any[];
  getVoicemailTemplateListRequest: RequestState;
  getVoicemailTemplateListResponse: VoicemailTemplateListResponse;
  getSignedUrlForVoicemailTemplateRequest: RequestState;
  getSignedUrlForVoicemailTemplateResponse: GetSignedUrlForVoicemailTemplateResponse;
  /** Signed URLs keyed by template ID - for table play per row */
  getSignedUrlForVoicemailTemplateResponses: Record<
    number,
    GetSignedUrlForVoicemailTemplateResponse
  >;
  /** Template ID currently being fetched (for row-specific loading spinner) */
  getSignedUrlForVoicemailTemplatePendingId: number | null;
  /** Call logs filter state (caller, source, outcome, date range) */
  callLogsFilters: CallLogsFiltersState;
  createVoicemailTemplateRequest: RequestState;
  updateVoicemailTemplateRequest: RequestState;
  deleteVoicemailTemplateRequest: RequestState;
  getPreSignedUrlRequest: RequestState;
  getPreSignedUrlResponse: GetPreSignedUrlResponse;
  callLogsListCountsRequest: RequestState;
  callLogsListCountsResponse: CallLogsListCountsResponse;
  assignCallOutcomeRequest: RequestState;
  getReportsStatsRequest: RequestState;
  getReportIndividualListRequest: RequestState;
  getReportTeamListRequest: RequestState;
  exportCallReportsRequest: RequestState;
  exportCallReportsResponse: {
    message: string;
    status: number;
  };
  exportCallLogsDetailedRequest: RequestState;
  exportCallLogsDetailedResponse: {
    message: string;
    status: number;
  };
  getReportsStatsResponse: ReportsStatsResponse;
  getReportIndividualListResponse: ReportIndividualListResponse;
  getReportTeamListResponse: ReportTeamListResponse;
  getCallLogsProspectDrawerHeaderRequest: RequestState;
  getCallLogsProspectDrawerHeaderResponse: CallsResponse;
  getCallLogsProspectProfileSectionRequest: RequestState;
  getCallLogsProspectProfileSectionResponse: any;
  createCallLogsNoteRequest: RequestState;
  createCallLogsNoteResponse: ResponseSuccess;
  updateCallLogsNoteRequest: RequestState;
  updateCallLogsNoteResponse: ResponseSuccess;
  getPhoneCallMentionableUsersRequest: RequestState;
  getPhoneCallMentionableUsersResponse: ResponseSuccess;
  singleCallDetails: CallLog;
  attachProspectToCallRequest: RequestState;
  initatedCallFromTask: {
    taskId: number;
    allowedPhoneNumbers: string[] | null;
  } | null;
  duringCallNote: string | null;
  ongoingPhoneNumber: string | null;
  ongoingSelectedProspect: ProspectOption | null;
  ongoingSelectedCountryISOCode: string | null;
}

/** True when profile section payload is for the same dialer call as the saved note. */
const callLogsProfileMatchesSavedNoteCall = (
  profile: Record<string, unknown> | null | undefined,
  savedCallId: number,
): boolean => {
  if (
    !profile ||
    savedCallId == null ||
    !Number.isFinite(Number(savedCallId))
  ) {
    return false;
  }
  const sid = Number(savedCallId);
  const candidates = [
    profile.id,
    profile.dialerCallLogId,
    profile.callId != null
      ? Number(profile.callId as string | number)
      : undefined,
  ].filter((v) => v != null && v !== '');
  return candidates.some((c) => Number(c) === sid);
};

/** Merge create/update-note API result + request args into cached call details (avoids refetch). */
const mergeCallLogsProfileSectionNote = (
  profile: Record<string, unknown>,
  arg: createCallLogsNotePayload | updateCallLogsNotePayload,
  apiPayload: unknown,
): Record<string, unknown> => {
  const existing = (profile.noteResponse ?? profile.note ?? {}) as Record<
    string,
    unknown
  >;

  const unwrapApiNote = (raw: unknown): Record<string, unknown> | null => {
    if (!raw || typeof raw !== 'object') return null;
    const obj = raw as Record<string, unknown>;
    const nested = obj.note;
    if (nested && typeof nested === 'object') {
      return nested as Record<string, unknown>;
    }
    return obj;
  };

  const fromApi = unwrapApiNote(apiPayload);

  const merged: Record<string, unknown> = {
    ...existing,
    ...(fromApi ?? {}),
    content:
      (fromApi?.content as string | undefined) ??
      arg.content ??
      (existing.content as string | undefined),
    visibility:
      (fromApi?.visibility as string | undefined) ??
      arg.visibility ??
      (existing.visibility as string | undefined),
  };

  return {
    ...profile,
    noteResponse: merged,
    note: merged,
  };
};

const initialState: DialerState = {
  // ** Dialer Request States **
  getCountriesOptionsForNumbersSearchRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  searchPhoneNumbersRequest: {
    status: RequestStatus.Pending, // To prevent jerk on mount.
    message: null,
    error: null,
  },
  getPhoneNumbersCartRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  purchasePhoneNumbersCartRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  purchasePhoneNumbersCartResponse: null,
  getContactsListForManualCallDialingRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  createTwillioVoiceTokenRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  initiateCallRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  endInitiatedCallRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  pickupCallRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getMyPhoneNumberListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getPhoneNumberLabelsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getPhoneNumberDetailsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  assignPhoneNumberRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  bulkAssignPhoneNumberRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  setPhoneNumberLabelRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  bulkSetPhoneNumberLabelRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  bulkUpdatePhoneNumberSettingsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updatePhoneNumberSettingsRequest: {},
  setPrimaryPhoneNumberRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  deletePhoneNumberRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  revokePhoneNumberReleaseRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getDialerPhoneNumberListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getVoicemailTemplateMinimalListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  dropVoicemailTemplateRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  callRecordingRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  listAllCallLogsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  listMissedCallsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  listIncomingVoicemailsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  deleteCallLogRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  markUnseenMissedCallCountRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getCallOutcomeListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getCallOutcomeListResponse: null,

  // ** Dialer Value States **
  phoneNumbersInCart: [],
  hasErrorsInContactDetailsForm: true,
  countriesOptionsForNumbersSearch: [],
  phoneNumbersList: [],
  cartUniqueConfiguration: null,
  isRegionChangedInLastSearch: false,
  lastSearchRegion: null,
  addPhoneNumbersToCartRequest: {},
  removePhoneNumberFromCartRequest: {},
  twillioVoiceToken: null,
  initiatedCallId: null,
  firebaseConnectionDisabledByUser: false,
  myPhoneNumbersList: null,
  countryCodeOptionsListForFilters: null,
  regionOptionsListForFilters: [],
  myPhoneNumbersListFilters: {
    countryCodes: [],
    regions: [],
    teamMemberIds: [],
    purchaseDate: null,
  },
  teamMembersList: [],
  phoneNumberLabels: [],
  activeNumberSettings: null,
  showDialer: false,
  showIncomingCall: false,
  callLogsList: null,
  prospectMinimalListResponse: null,
  dialerPhoneNumberList: [],
  voicemailTemplateMinimalList: null,
  dialerControlsState: {
    microphone: false,
    hold: false,
    record: false,
    voicemail: false,
    keypad: false,
  },
  duringCallNote: '',
  twillioVoiceTokenSessionHashId: null,
  listAllCallLogsResponse: null,
  listMissedCallsResponse: null,
  listIncomingVoicemailsResponse: null,
  activeCallLogsResponse: null,
  showMicrophoneAccessWarningModal: false,
  getNetworkDiagnosticTokenRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  networkDiagnosticIceServers: null,
  selectedCallbackProspect: null,
  currentCallManagementState: null,
  ongoingPhoneNumber: null as string | null,
  ongoingSelectedProspect: null as ProspectOption | null,
  ongoingSelectedCountryISOCode: null as string | null,
  callNetworkLevel: 4,
  activeCallLogsTab: 'all',
  missedCallCount: 0,
  markCallAsSeenRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSignedUrlForCallRecordingRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getVoicemailTemplateListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getVoicemailTemplateListResponse: null,
  getSignedUrlForCallRecordingResponse: null,
  getSignedUrlForCallRecordingResponses: {},
  getSignedUrlForVoicemailTemplateRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSignedUrlForVoicemailTemplateResponse: null,
  getSignedUrlForVoicemailTemplateResponses: {},
  getSignedUrlForVoicemailTemplatePendingId: null,
  createVoicemailTemplateRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateVoicemailTemplateRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  deleteVoicemailTemplateRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getPreSignedUrlRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getPreSignedUrlResponse: null,
  callLogsFilters: {
    callerHost: [],
    source: [],
    callOutcome: [],
    callDateStart: '',
    callDateEnd: '',
  },
  callLogsListCountsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  callLogsListCountsResponse: null,
  assignCallOutcomeRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getReportsStatsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getReportIndividualListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getReportTeamListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  exportCallReportsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  exportCallReportsResponse: null,
  exportCallLogsDetailedRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  exportCallLogsDetailedResponse: null,
  getReportsStatsResponse: null,
  getReportIndividualListResponse: null,
  getReportTeamListResponse: null,
  getCallLogsProspectDrawerHeaderRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getCallLogsProspectDrawerHeaderResponse: null,
  getCallLogsProspectProfileSectionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getCallLogsProspectProfileSectionResponse: null,
  createCallLogsNoteRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  createCallLogsNoteResponse: null,
  updateCallLogsNoteRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateCallLogsNoteResponse: null,
  getPhoneCallMentionableUsersRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getPhoneCallMentionableUsersResponse: null,
  singleCallDetails: null,
  attachProspectToCallRequest: {
    status: RequestStatus.Ideal,
    error: null,
    message: null,
  },
  initatedCallFromTask: null,
};

const dialerSlice = createSlice({
  name: 'dialer',
  initialState,
  reducers: {
    addPhoneNumberToCart: (
      state,
      action: PayloadAction<PhoneNumberCartItem>,
    ) => {
      const existingIndex = state.phoneNumbersInCart.findIndex(
        (item) => item.phoneNumber === action.payload.phoneNumber,
      );
      if (existingIndex === -1) {
        state.phoneNumbersInCart.push(action.payload);
      }
    },
    removePhoneNumberFromCart: (state, action: PayloadAction<string>) => {
      state.phoneNumbersInCart = state.phoneNumbersInCart.filter(
        (item) => item.phoneNumber !== action.payload,
      );
    },
    resetAddPhoneNumbersToCartRequest: (
      state,
      action: PayloadAction<string>,
    ) => {
      delete state.addPhoneNumbersToCartRequest[action.payload];
    },
    resetRemovePhoneNumberFromCartRequest: (
      state,
      action: PayloadAction<string>,
    ) => {
      delete state.removePhoneNumberFromCartRequest[action.payload];
    },
    resetPurchasePhoneNumbersCartRequest: (state) => {
      state.purchasePhoneNumbersCartRequest =
        initialState.purchasePhoneNumbersCartRequest;
      state.purchasePhoneNumbersCartResponse =
        initialState.purchasePhoneNumbersCartResponse;
    },
    resetPhoneNumbersCart: (state) => {
      state.phoneNumbersInCart = initialState.phoneNumbersInCart;
    },
    resetGetContactsListForManualCallDialingRequest: (state) => {
      state.getContactsListForManualCallDialingRequest =
        initialState.getContactsListForManualCallDialingRequest;
    },
    resetCreateTwillioVoiceTokenRequest: (state) => {
      state.createTwillioVoiceTokenRequest =
        initialState.createTwillioVoiceTokenRequest;
    },
    resetInitiateCallRequest: (state) => {
      state.initiateCallRequest = initialState.initiateCallRequest;
    },
    resetEndInitiatedCallRequest: (state) => {
      state.endInitiatedCallRequest = initialState.endInitiatedCallRequest;
    },
    resetAttachProspectToCallRequest: (state) => {
      state.attachProspectToCallRequest =
        initialState.attachProspectToCallRequest;
    },
    updateHasErrorsInContactDetailsForm: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.hasErrorsInContactDetailsForm = action.payload;
    },
    updateCartUniqueConfiguration: (
      state,
      action: PayloadAction<CartUniqueConfiguration | null>,
    ) => {
      if (action.payload === null) {
        state.cartUniqueConfiguration = null;
        return;
      }
      state.cartUniqueConfiguration = {
        countryISOCode: action.payload.countryISOCode,
        numberType: action.payload.numberType,
        isLocalityRequired: action.payload.isLocalityRequired,
      };
    },
    setIsRegionChangedInLastSearch: (state, action: PayloadAction<boolean>) => {
      state.isRegionChangedInLastSearch = action.payload;
    },
    setLastSearchRegion: (state, action: PayloadAction<string | null>) => {
      state.lastSearchRegion = action.payload;
    },
    setMyPhoneNumbersListFilters: (
      state,
      action: PayloadAction<PhoneNumberFilters>,
    ) => {
      state.myPhoneNumbersListFilters = action.payload;
    },
    resetBulkSetPhoneNumberLabelRequest: (state) => {
      state.bulkSetPhoneNumberLabelRequest =
        initialState.bulkSetPhoneNumberLabelRequest;
    },
    resetSetPhoneNumberLabelRequest: (state) => {
      state.setPhoneNumberLabelRequest =
        initialState.setPhoneNumberLabelRequest;
    },
    resetAssignPhoneNumberRequest: (state) => {
      state.assignPhoneNumberRequest = initialState.assignPhoneNumberRequest;
    },
    resetBulkAssignPhoneNumberRequest: (state) => {
      state.bulkAssignPhoneNumberRequest =
        initialState.bulkAssignPhoneNumberRequest;
    },
    resetBulkUpdatePhoneNumberSettingsRequest: (state) => {
      state.bulkUpdatePhoneNumberSettingsRequest =
        initialState.bulkUpdatePhoneNumberSettingsRequest;
    },
    resetUpdatePhoneNumberSettingsRequest: (
      state,
      action: PayloadAction<string>,
    ) => {
      const code = action.payload;
      if (state.updatePhoneNumberSettingsRequest[code]) {
        delete state.updatePhoneNumberSettingsRequest[code];
      }
    },
    resetSetPrimaryPhoneNumberRequest: (state) => {
      state.setPrimaryPhoneNumberRequest =
        initialState.setPrimaryPhoneNumberRequest;
    },
    resetDeletePhoneNumberRequest: (state) => {
      state.deletePhoneNumberRequest = initialState.deletePhoneNumberRequest;
    },
    resetRevokePhoneNumberReleaseRequest: (state) => {
      state.revokePhoneNumberReleaseRequest =
        initialState.revokePhoneNumberReleaseRequest;
    },
    setShowDialer: (state, action: PayloadAction<boolean>) => {
      state.showDialer = action.payload;
    },
    setShowIncomingCall: (state, action: PayloadAction<boolean>) => {
      state.showIncomingCall = action.payload;
    },
    resetProspectMinimalListResponse: (state) => {
      state.prospectMinimalListResponse =
        initialState.prospectMinimalListResponse;
    },
    resetDialerPhoneNumberList: (state) => {
      state.dialerPhoneNumberList = initialState.dialerPhoneNumberList;
    },
    resetGetVoicemailTemplateMinimalListRequest: (state) => {
      state.getVoicemailTemplateMinimalListRequest =
        initialState.getVoicemailTemplateMinimalListRequest;
    },
    resetDropVoicemailTemplateRequest: (state) => {
      state.dropVoicemailTemplateRequest =
        initialState.dropVoicemailTemplateRequest;
    },
    resetCallRecordingRequest: (state) => {
      state.callRecordingRequest = initialState.callRecordingRequest;
    },
    setDialerControlsState: (
      state,
      action: PayloadAction<Partial<DialerControlsState>>,
    ) => {
      state.dialerControlsState = {
        ...state.dialerControlsState,
        ...action.payload,
      };
    },
    enableCallRecording: (state) => {
      state.dialerControlsState.record = true;
    },
    toggleDialerControl: (
      state,
      action: PayloadAction<keyof DialerControlsState>,
    ) => {
      const control = action.payload;
      state.dialerControlsState[control] = !state.dialerControlsState[control];
    },
    resetDialerControlsState: (state) => {
      state.dialerControlsState = initialState.dialerControlsState;
    },
    setDuringCallNote: (state, action: PayloadAction<string>) => {
      state.duringCallNote = action.payload;
    },
    clearDuringCallNote: (state) => {
      state.duringCallNote = '';
    },
    setInitiatedCallId: (state, action: PayloadAction<string>) => {
      state.initiatedCallId = action.payload;
      state.firebaseConnectionDisabledByUser = false;
    },
    clearInitiatedCallId: (state) => {
      state.initiatedCallId = null;
      state.firebaseConnectionDisabledByUser = true;
    },
    setShowMicrophoneAccessWarningModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.showMicrophoneAccessWarningModal = action.payload;
    },
    resetNetworkDiagnosticIceServers: (state) => {
      state.networkDiagnosticIceServers =
        initialState.networkDiagnosticIceServers;
    },
    resetGetNetworkDiagnosticTokenRequest: (state) => {
      state.getNetworkDiagnosticTokenRequest =
        initialState.getNetworkDiagnosticTokenRequest;
    },
    setSelectedCallbackProspect: (state, action: PayloadAction<CallLog[]>) => {
      state.selectedCallbackProspect = action.payload;
    },
    resetSelectedCallbackProspect: (state) => {
      state.selectedCallbackProspect = initialState.selectedCallbackProspect;
    },
    setCurrentCallManagementState: (
      state,
      action: PayloadAction<CallManagementState>,
    ) => {
      state.currentCallManagementState = action.payload;
    },
    setOngoingCallInfo: (
      state,
      action: PayloadAction<{
        phoneNumber: string;
        prospect: ProspectOption | null;
        countryISOCode: string;
      }>,
    ) => {
      state.ongoingPhoneNumber = action.payload.phoneNumber;
      state.ongoingSelectedProspect = action.payload.prospect;
      state.ongoingSelectedCountryISOCode = action.payload.countryISOCode;
    },
    resetOngoingCallInfo: (state) => {
      state.ongoingPhoneNumber = initialState.ongoingPhoneNumber;
      state.ongoingSelectedProspect = initialState.ongoingSelectedProspect;
      state.ongoingSelectedCountryISOCode =
        initialState.ongoingSelectedCountryISOCode;
    },
    setCallNetworkLevel: (state, action: PayloadAction<number>) => {
      state.callNetworkLevel = Math.max(0, Math.min(4, action.payload));
    },
    resetCreateVoicemailTemplateRequest: (state) => {
      state.createVoicemailTemplateRequest =
        initialState.createVoicemailTemplateRequest;
    },
    setActiveCallLogsTab: (state, action: PayloadAction<string>) => {
      state.activeCallLogsTab = action.payload;
      const tab = action.payload;
      if (tab === 'missed') {
        state.activeCallLogsResponse = state.listMissedCallsResponse;
      } else if (tab === 'voicemails') {
        state.activeCallLogsResponse = state.listIncomingVoicemailsResponse;
      } else {
        state.activeCallLogsResponse = state.listAllCallLogsResponse;
      }
    },
    resetDeleteCallLogRequest: (state) => {
      state.deleteCallLogRequest = initialState.deleteCallLogRequest;
    },
    resetGetSignedUrlForVoicemailTemplateRequest: (state) => {
      state.getSignedUrlForVoicemailTemplateRequest =
        initialState.getSignedUrlForVoicemailTemplateRequest;
    },
    setCallLogsFilters: (
      state,
      action: PayloadAction<CallLogsFiltersState>,
    ) => {
      state.callLogsFilters = action.payload;
    },
    resetCallLogsFilters: (state) => {
      state.callLogsFilters = initialState.callLogsFilters;
    },
    resetUpdateVoicemailTemplateRequest: (state) => {
      state.updateVoicemailTemplateRequest =
        initialState.updateVoicemailTemplateRequest;
    },
    resetGetSignedUrlForVoicemailTemplateResponses: (state) => {
      state.getSignedUrlForVoicemailTemplateResponses = {};
    },
    resetAssignCallOutcomeRequest: (state) => {
      state.assignCallOutcomeRequest = initialState.assignCallOutcomeRequest;
    },
    resetDeleteVoicemailTemplateRequest: (state) => {
      state.deleteVoicemailTemplateRequest =
        initialState.deleteVoicemailTemplateRequest;
    },
    resetMarkUnseenMissedCallCountRequest: (state) => {
      state.markUnseenMissedCallCountRequest =
        initialState.markUnseenMissedCallCountRequest;
    },
    resetMarkCallAsSeenRequest: (state) => {
      state.markCallAsSeenRequest = initialState.markCallAsSeenRequest;
    },
    // Reports Stats
    resetGetReportsStatsRequest: (state) => {
      state.getReportsStatsRequest = initialState.getReportsStatsRequest;
    },
    // Report Individual List
    resetGetReportIndividualListRequest: (state) => {
      state.getReportIndividualListRequest =
        initialState.getReportIndividualListRequest;
    },
    // Report Team List
    resetGetReportTeamListRequest: (state) => {
      state.getReportTeamListRequest = initialState.getReportTeamListRequest;
    },
    // Export Call Reports
    resetExportCallReportsRequest: (state) => {
      state.exportCallReportsRequest = initialState.exportCallReportsRequest;
    },
    // Export Call Logs Detailed
    resetExportCallLogsDetailedRequest: (state) => {
      state.exportCallLogsDetailedRequest =
        initialState.exportCallLogsDetailedRequest;
    },
    resetGetCallLogsProspectDrawerHeaderRequest: (state) => {
      state.getCallLogsProspectDrawerHeaderRequest =
        initialState.getCallLogsProspectDrawerHeaderRequest;
    },
    resetGetCallLogsProspectProfileSectionRequest: (state) => {
      state.getCallLogsProspectProfileSectionRequest =
        initialState.getCallLogsProspectProfileSectionRequest;
    },
    resetGetPhoneNumberDetailsRequest: (state) => {
      state.getPhoneNumberDetailsRequest =
        initialState.getPhoneNumberDetailsRequest;
    },
    setSingleCallDetails: (state, action: PayloadAction<CallLog>) => {
      state.singleCallDetails = action.payload;
    },
    resetCreateCallLogsNoteRequest: (state) => {
      state.createCallLogsNoteRequest = initialState.createCallLogsNoteRequest;
    },
    resetUpdateCallLogsNoteRequest: (state) => {
      state.updateCallLogsNoteRequest = initialState.updateCallLogsNoteRequest;
    },
    setInitiatedCallFromTask: (
      state,
      action: PayloadAction<{
        taskId: number;
        allowedPhoneNumbers: string[] | null;
      }>,
    ) => {
      state.initatedCallFromTask = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get Countries Options For Numbers Search
    builder.addCase(
      getCountriesOptionsForNumbersSearchRequest.pending,
      (state) => {
        state.getCountriesOptionsForNumbersSearchRequest.status =
          RequestStatus.Pending;
        state.getCountriesOptionsForNumbersSearchRequest.error = null;
        state.getCountriesOptionsForNumbersSearchRequest.message = null;
      },
    );
    builder.addCase(
      getCountriesOptionsForNumbersSearchRequest.fulfilled,
      (state, action) => {
        state.getCountriesOptionsForNumbersSearchRequest.message =
          action.payload.message;
        state.getCountriesOptionsForNumbersSearchRequest.status =
          RequestStatus.Succeeded;
        state.getCountriesOptionsForNumbersSearchRequest.error = null;

        state.countriesOptionsForNumbersSearch = action?.payload?.payload;
      },
    );
    builder.addCase(
      getCountriesOptionsForNumbersSearchRequest.rejected,
      (state, action) => {
        state.getCountriesOptionsForNumbersSearchRequest.message =
          action.payload.message;
        state.getCountriesOptionsForNumbersSearchRequest.status =
          RequestStatus.Failed;
        state.getCountriesOptionsForNumbersSearchRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
    // Search Phone Numbers
    builder.addCase(searchPhoneNumbersRequest.pending, (state) => {
      state.searchPhoneNumbersRequest.status = RequestStatus.Pending;
      state.searchPhoneNumbersRequest.error = null;
      state.searchPhoneNumbersRequest.message = null;
    });
    builder.addCase(searchPhoneNumbersRequest.fulfilled, (state, action) => {
      state.searchPhoneNumbersRequest.message = action.payload.message;
      state.searchPhoneNumbersRequest.status = RequestStatus.Succeeded;
      state.searchPhoneNumbersRequest.error = null;
      state.phoneNumbersList = action.payload.payload || [];
    });
    builder.addCase(searchPhoneNumbersRequest.rejected, (state, action) => {
      state.searchPhoneNumbersRequest.message = action.payload.message;
      state.searchPhoneNumbersRequest.status = RequestStatus.Failed;
      state.searchPhoneNumbersRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // Add Phone Numbers To Cart
    builder.addCase(addPhoneNumbersToCartRequest.pending, (state, action) => {
      const { phoneNumber } = action.meta.arg;
      const normalizedPhoneNumber = phoneNumber.replace(/\s/g, '');
      if (!state.addPhoneNumbersToCartRequest[normalizedPhoneNumber]) {
        state.addPhoneNumbersToCartRequest[normalizedPhoneNumber] = {
          status: RequestStatus.Ideal,
          message: null,
          error: null,
        };
      }
      state.addPhoneNumbersToCartRequest[normalizedPhoneNumber].status =
        RequestStatus.Pending;
      state.addPhoneNumbersToCartRequest[normalizedPhoneNumber].error = null;
      state.addPhoneNumbersToCartRequest[normalizedPhoneNumber].message = null;
    });
    builder.addCase(addPhoneNumbersToCartRequest.fulfilled, (state, action) => {
      const {
        phoneNumber,
        region,
        locality,
        countryCode,
        pricePerQuarter,
        numberType,
      } = action.meta.arg;
      const normalizedPhoneNumber = phoneNumber.replace(/\s/g, '');
      if (state.addPhoneNumbersToCartRequest[normalizedPhoneNumber]) {
        state.addPhoneNumbersToCartRequest[normalizedPhoneNumber].status =
          RequestStatus.Succeeded;
        state.addPhoneNumbersToCartRequest[normalizedPhoneNumber].message =
          action.payload.message;
        state.addPhoneNumbersToCartRequest[normalizedPhoneNumber].error = null;
      }
      // Update cart only on successful API response
      const existingIndex = state.phoneNumbersInCart.findIndex(
        (item) => item.phoneNumber.replace(/\s/g, '') === normalizedPhoneNumber,
      );
      if (existingIndex === -1) {
        const formattedPhoneNumber = formatPhoneNumber(
          phoneNumber,
          countryCode,
        );
        const formattedRegionValue = formatRegion(
          locality || '',
          region || '',
          countryCode,
        );
        const cartRecordId = action.payload?.payload?.id ?? 0;
        state.phoneNumbersInCart.push({
          id: cartRecordId,
          phoneNumber: formattedPhoneNumber,
          formattedRegion: formattedRegionValue,
          numberType: capitalizeFirst(numberType),
          pricePerQuarter: formatPrice(pricePerQuarter),
          countryISOCode: countryCode,
          region: region || null,
          locality: locality || null,
          isFreeNumber: false,
        });
      }
    });
    builder.addCase(addPhoneNumbersToCartRequest.rejected, (state, action) => {
      const { phoneNumber } = action.meta.arg;
      const normalizedPhoneNumber = phoneNumber.replace(/\s/g, '');
      if (state.addPhoneNumbersToCartRequest[normalizedPhoneNumber]) {
        state.addPhoneNumbersToCartRequest[normalizedPhoneNumber].status =
          RequestStatus.Failed;
        state.addPhoneNumbersToCartRequest[normalizedPhoneNumber].message =
          action.payload.message;
        state.addPhoneNumbersToCartRequest[normalizedPhoneNumber].error =
          !action.payload.isHandled && action.payload;
      }
    });
    // Remove Phone Number From Cart
    builder.addCase(
      removePhoneNumberFromCartRequest.pending,
      (state, action) => {
        const { phoneNumber } = action.meta.arg;
        if (phoneNumber) {
          const normalizedPhoneNumber = phoneNumber.replace(/\s/g, '');
          if (!state.removePhoneNumberFromCartRequest[normalizedPhoneNumber]) {
            state.removePhoneNumberFromCartRequest[normalizedPhoneNumber] = {
              status: RequestStatus.Ideal,
              message: null,
              error: null,
            };
          }
          state.removePhoneNumberFromCartRequest[normalizedPhoneNumber].status =
            RequestStatus.Pending;
          state.removePhoneNumberFromCartRequest[
            normalizedPhoneNumber
          ].error = null;
          state.removePhoneNumberFromCartRequest[
            normalizedPhoneNumber
          ].message = null;
        } else {
          // Clear all case - track with special key
          const clearAllKey = '__clearAll__';
          if (!state.removePhoneNumberFromCartRequest[clearAllKey]) {
            state.removePhoneNumberFromCartRequest[clearAllKey] = {
              status: RequestStatus.Ideal,
              message: null,
              error: null,
            };
          }
          state.removePhoneNumberFromCartRequest[clearAllKey].status =
            RequestStatus.Pending;
          state.removePhoneNumberFromCartRequest[clearAllKey].error = null;
          state.removePhoneNumberFromCartRequest[clearAllKey].message = null;
        }
      },
    );
    builder.addCase(
      removePhoneNumberFromCartRequest.fulfilled,
      (state, action) => {
        const { phoneNumber } = action.meta.arg;
        if (phoneNumber) {
          const normalizedPhoneNumber = phoneNumber.replace(/\s/g, '');
          if (state.removePhoneNumberFromCartRequest[normalizedPhoneNumber]) {
            state.removePhoneNumberFromCartRequest[
              normalizedPhoneNumber
            ].status = RequestStatus.Succeeded;
            state.removePhoneNumberFromCartRequest[
              normalizedPhoneNumber
            ].message = action.payload.message;
            state.removePhoneNumberFromCartRequest[
              normalizedPhoneNumber
            ].error = null;
          }
          // Remove from cart only on successful API response
          state.phoneNumbersInCart = state.phoneNumbersInCart.filter(
            (item) =>
              item.phoneNumber.replace(/\s/g, '') !== normalizedPhoneNumber,
          );
        } else {
          // Clear all case - phoneNumber is undefined
          const clearAllKey = '__clearAll__';
          if (state.removePhoneNumberFromCartRequest[clearAllKey]) {
            state.removePhoneNumberFromCartRequest[clearAllKey].status =
              RequestStatus.Succeeded;
            state.removePhoneNumberFromCartRequest[clearAllKey].message =
              action.payload.message;
            state.removePhoneNumberFromCartRequest[clearAllKey].error = null;
          }
          // Clear entire cart only on successful API response
          state.phoneNumbersInCart = [];
        }
      },
    );
    builder.addCase(
      removePhoneNumberFromCartRequest.rejected,
      (state, action) => {
        const { phoneNumber } = action.meta.arg;
        if (phoneNumber) {
          const normalizedPhoneNumber = phoneNumber.replace(/\s/g, '');
          if (state.removePhoneNumberFromCartRequest[normalizedPhoneNumber]) {
            state.removePhoneNumberFromCartRequest[
              normalizedPhoneNumber
            ].status = RequestStatus.Failed;
            state.removePhoneNumberFromCartRequest[
              normalizedPhoneNumber
            ].message = action.payload.message;
            state.removePhoneNumberFromCartRequest[
              normalizedPhoneNumber
            ].error = !action.payload.isHandled && action.payload;
          }
        } else {
          // Clear all case - phoneNumber is undefined
          const clearAllKey = '__clearAll__';
          if (state.removePhoneNumberFromCartRequest[clearAllKey]) {
            state.removePhoneNumberFromCartRequest[clearAllKey].status =
              RequestStatus.Failed;
            state.removePhoneNumberFromCartRequest[clearAllKey].message =
              action.payload.message;
            state.removePhoneNumberFromCartRequest[clearAllKey].error =
              !action.payload.isHandled && action.payload;
          }
        }
      },
    );
    // Get Phone Numbers Cart
    builder.addCase(getPhoneNumbersCartRequest.pending, (state) => {
      state.getPhoneNumbersCartRequest.status = RequestStatus.Pending;
      state.getPhoneNumbersCartRequest.error = null;
      state.getPhoneNumbersCartRequest.message = null;
    });
    builder.addCase(getPhoneNumbersCartRequest.fulfilled, (state, action) => {
      state.getPhoneNumbersCartRequest.message = action.payload.message;
      state.getPhoneNumbersCartRequest.status = RequestStatus.Succeeded;
      state.getPhoneNumbersCartRequest.error = null;
      const cartItems = action.payload.payload as
        | PhoneNumberCartItemFromAPI[]
        | null;
      state.phoneNumbersInCart = transformCartItemsFromAPI(cartItems);
    });
    builder.addCase(getPhoneNumbersCartRequest.rejected, (state, action) => {
      state.getPhoneNumbersCartRequest.message = action.payload.message;
      state.getPhoneNumbersCartRequest.status = RequestStatus.Failed;
      state.getPhoneNumbersCartRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // Purchase Phone Numbers Cart
    builder.addCase(purchasePhoneNumbersCartRequest.pending, (state) => {
      state.purchasePhoneNumbersCartRequest.status = RequestStatus.Pending;
      state.purchasePhoneNumbersCartRequest.error = null;
      state.purchasePhoneNumbersCartRequest.message = null;
    });
    builder.addCase(
      purchasePhoneNumbersCartRequest.fulfilled,
      (state, action) => {
        state.purchasePhoneNumbersCartRequest.message = action.payload.message;
        state.purchasePhoneNumbersCartRequest.status = RequestStatus.Succeeded;
        state.purchasePhoneNumbersCartRequest.error = null;
        state.purchasePhoneNumbersCartResponse =
          (action.payload as any)?.payload || null;
      },
    );
    builder.addCase(
      purchasePhoneNumbersCartRequest.rejected,
      (state, action) => {
        state.purchasePhoneNumbersCartRequest.message = action.payload.message;
        state.purchasePhoneNumbersCartRequest.status = RequestStatus.Failed;
        state.purchasePhoneNumbersCartRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
    // Get Contacts List For Manual Call Dialing
    builder.addCase(
      getContactsListForManualCallDialingRequest.pending,
      (state) => {
        state.getContactsListForManualCallDialingRequest.status =
          RequestStatus.Pending;
        state.getContactsListForManualCallDialingRequest.error = null;
        state.getContactsListForManualCallDialingRequest.message = null;
      },
    );
    builder.addCase(
      getContactsListForManualCallDialingRequest.fulfilled,
      (state, action) => {
        state.getContactsListForManualCallDialingRequest.message =
          action.payload.message;
        state.getContactsListForManualCallDialingRequest.status =
          RequestStatus.Succeeded;
        state.prospectMinimalListResponse = action.payload?.payload || null;
        state.getContactsListForManualCallDialingRequest.error = null;
      },
    );
    builder.addCase(
      getContactsListForManualCallDialingRequest.rejected,
      (state, action) => {
        state.getContactsListForManualCallDialingRequest.message =
          action.payload.message;
        state.getContactsListForManualCallDialingRequest.status =
          RequestStatus.Failed;
        state.getContactsListForManualCallDialingRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
    // Create Twillio Voice Token
    builder.addCase(createTwillioVoiceTokenRequest.pending, (state) => {
      state.createTwillioVoiceTokenRequest.status = RequestStatus.Pending;
      state.createTwillioVoiceTokenRequest.error = null;
      state.createTwillioVoiceTokenRequest.message = null;
    });
    builder.addCase(
      createTwillioVoiceTokenRequest.fulfilled,
      (state, action) => {
        state.createTwillioVoiceTokenRequest.message = action.payload.message;
        state.createTwillioVoiceTokenRequest.status = RequestStatus.Succeeded;
        state.createTwillioVoiceTokenRequest.error = null;
        state.twillioVoiceToken = action.payload.payload?.token || null;
        state.twillioVoiceTokenSessionHashId =
          action.payload.payload?.sessionHashId || null;
      },
    );
    builder.addCase(
      createTwillioVoiceTokenRequest.rejected,
      (state, action) => {
        state.createTwillioVoiceTokenRequest.message = action.payload.message;
        state.createTwillioVoiceTokenRequest.status = RequestStatus.Failed;
        state.createTwillioVoiceTokenRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
    // Initiate Call
    builder.addCase(initiateCallRequest.pending, (state) => {
      state.initiateCallRequest.status = RequestStatus.Pending;
      state.initiateCallRequest.error = null;
      state.initiateCallRequest.message = null;
    });
    builder.addCase(initiateCallRequest.fulfilled, (state, action) => {
      state.initiateCallRequest.message = action.payload.message;
      state.initiateCallRequest.status = RequestStatus.Succeeded;
      state.initiateCallRequest.error = null;
      state.initiatedCallId = action?.payload?.payload?.callId || null;
      state.firebaseConnectionDisabledByUser = false;
    });
    builder.addCase(initiateCallRequest.rejected, (state, action) => {
      state.initiateCallRequest.message = action.payload.message;
      state.initiateCallRequest.status = RequestStatus.Failed;
      state.initiateCallRequest.error =
        !action.payload.isHandled && action.payload;
      state.initiatedCallId = null;
    });
    // End Initiated Call
    builder.addCase(endInitiatedCallRequest.pending, (state) => {
      state.endInitiatedCallRequest.status = RequestStatus.Pending;
      state.endInitiatedCallRequest.error = null;
      state.endInitiatedCallRequest.message = null;
    });
    builder.addCase(endInitiatedCallRequest.fulfilled, (state, action) => {
      state.endInitiatedCallRequest.message = action.payload.message;
      state.endInitiatedCallRequest.status = RequestStatus.Succeeded;
      state.endInitiatedCallRequest.error = null;
      state.initiatedCallId = null;
    });
    builder.addCase(endInitiatedCallRequest.rejected, (state, action) => {
      state.endInitiatedCallRequest.message = action.payload.message;
      state.endInitiatedCallRequest.status = RequestStatus.Failed;
      state.endInitiatedCallRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // Pickup Call
    builder.addCase(pickupCallRequest.pending, (state) => {
      state.pickupCallRequest.status = RequestStatus.Pending;
      state.pickupCallRequest.error = null;
      state.pickupCallRequest.message = null;
    });
    builder.addCase(pickupCallRequest.fulfilled, (state, action) => {
      state.pickupCallRequest.status = RequestStatus.Succeeded;
      state.pickupCallRequest.message = action.payload.message;
      state.pickupCallRequest.error = null;
    });
    builder.addCase(pickupCallRequest.rejected, (state, action) => {
      state.pickupCallRequest.status = RequestStatus.Failed;
      state.pickupCallRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // Get My Phone Number List
    builder.addCase(getMyPhoneNumberListAction.pending, (state) => {
      state.getMyPhoneNumberListRequest.status = RequestStatus.Pending;
      state.getMyPhoneNumberListRequest.error = null;
      state.getMyPhoneNumberListRequest.message = null;
    });
    builder.addCase(getMyPhoneNumberListAction.fulfilled, (state, action) => {
      state.getMyPhoneNumberListRequest.message = action.payload.message;
      state.getMyPhoneNumberListRequest.status = RequestStatus.Succeeded;
      state.getMyPhoneNumberListRequest.error = null;
      state.myPhoneNumbersList = action.payload
        .payload as PhoneNumberListResponse | null;
    });
    builder.addCase(getMyPhoneNumberListAction.rejected, (state, action) => {
      state.getMyPhoneNumberListRequest.message = action.payload.message;
      state.getMyPhoneNumberListRequest.status = RequestStatus.Failed;
      state.getMyPhoneNumberListRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Dialer Phone Number List
    builder.addCase(getDialerPhoneNumberListRequest.pending, (state) => {
      state.getDialerPhoneNumberListRequest.status = RequestStatus.Pending;
      state.getDialerPhoneNumberListRequest.error = null;
      state.getDialerPhoneNumberListRequest.message = null;
    });
    builder.addCase(
      getDialerPhoneNumberListRequest.fulfilled,
      (state, action) => {
        state.getDialerPhoneNumberListRequest.message = action.payload.message;
        state.getDialerPhoneNumberListRequest.status = RequestStatus.Succeeded;
        state.getDialerPhoneNumberListRequest.error = null;
        state.dialerPhoneNumberList =
          (action.payload.payload as DialerPhoneNumberItem[]) || [];
      },
    );
    builder.addCase(
      getDialerPhoneNumberListRequest.rejected,
      (state, action) => {
        state.getDialerPhoneNumberListRequest.message = action.payload.message;
        state.getDialerPhoneNumberListRequest.status = RequestStatus.Failed;
        state.getDialerPhoneNumberListRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    builder.addCase(
      getCountryCodeOptionsListForFiltersRequest.fulfilled,
      (state, action) => {
        state.countryCodeOptionsListForFilters =
          action?.payload?.payload?.countryCodes;
      },
    );
    builder.addCase(
      getRegionOptionsListForFiltersRequest.fulfilled,
      (state, action) => {
        state.regionOptionsListForFilters = action?.payload?.payload?.regions;
      },
    );
    builder.addCase(getUsersListRequest.fulfilled, (state, action) => {
      state.teamMembersList = flattenTeamMembers(
        action?.payload?.payload ?? [],
      );
    });
    builder.addCase(getPhoneNumberLabelsRequest.pending, (state) => {
      state.getPhoneNumberLabelsRequest.status = RequestStatus.Pending;
      state.getPhoneNumberLabelsRequest.error = null;
      state.getPhoneNumberLabelsRequest.message = null;
    });
    builder.addCase(getPhoneNumberLabelsRequest.fulfilled, (state, action) => {
      state.getPhoneNumberLabelsRequest.message = action?.payload?.message;
      state.getPhoneNumberLabelsRequest.status = RequestStatus.Succeeded;
      state.getPhoneNumberLabelsRequest.error = null;
      state.phoneNumberLabels = action?.payload?.payload?.labels || [];
    });
    builder.addCase(getPhoneNumberLabelsRequest.rejected, (state, action) => {
      state.getPhoneNumberLabelsRequest.message = action?.payload?.message;
      state.getPhoneNumberLabelsRequest.status = RequestStatus.Failed;
      state.getPhoneNumberLabelsRequest.error =
        !action?.payload?.isHandled && action?.payload;
    });
    // Get Phone Number Details
    builder.addCase(getPhoneNumberDetailsRequest.pending, (state) => {
      state.getPhoneNumberDetailsRequest.status = RequestStatus.Pending;
      state.getPhoneNumberDetailsRequest.error = null;
      state.getPhoneNumberDetailsRequest.message = null;
    });
    builder.addCase(getPhoneNumberDetailsRequest.fulfilled, (state, action) => {
      state.getPhoneNumberDetailsRequest.message = action.payload.message;
      state.getPhoneNumberDetailsRequest.status = RequestStatus.Succeeded;
      state.getPhoneNumberDetailsRequest.error = null;
      state.activeNumberSettings = action.payload
        .payload as GetPhoneNumberSettingsResponse | null;
    });
    builder.addCase(getPhoneNumberDetailsRequest.rejected, (state, action) => {
      state.getPhoneNumberDetailsRequest.message = action.payload.message;
      state.getPhoneNumberDetailsRequest.status = RequestStatus.Failed;
      state.getPhoneNumberDetailsRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // Assign Phone Number
    builder.addCase(assignPhoneNumberRequest.pending, (state) => {
      state.assignPhoneNumberRequest.status = RequestStatus.Pending;
      state.assignPhoneNumberRequest.error = null;
      state.assignPhoneNumberRequest.message = null;
    });
    builder.addCase(assignPhoneNumberRequest.fulfilled, (state, action) => {
      state.assignPhoneNumberRequest.message = action.payload.message;
      state.assignPhoneNumberRequest.status = RequestStatus.Succeeded;
      state.assignPhoneNumberRequest.error = null;

      // Update the assigned user in myPhoneNumbersList for the assigned phone numbers
      const {
        phoneNumberIds,
        userId,
        assignedUser: passedAssignedUser,
      } = action.meta.arg;

      // Use passed user details if available, otherwise try to find from teamMembersList
      const foundMember = state.teamMembersList?.find(
        (member) => member.id === userId,
      );
      const assignedUser =
        passedAssignedUser ||
        (foundMember
          ? {
              id: foundMember.id,
              firstName: foundMember.name,
              lastName: '',
              email: foundMember.email,
            }
          : null);

      if (state.myPhoneNumbersList?.phoneNumbers) {
        state.myPhoneNumbersList.phoneNumbers = state.myPhoneNumbersList.phoneNumbers.map(
          (item) => {
            if (phoneNumberIds.includes(item.id)) {
              if (assignedUser) {
                // Update with full user data (from passed details or teamMembersList)
                return {
                  ...item,
                  assignedUser: {
                    id: assignedUser.id,
                    firstName: assignedUser.firstName || '',
                    lastName: assignedUser.lastName || '',
                    email: assignedUser.email || '',
                  },
                };
              }
              // If user not found (fallback case),
              // update with just the userId - the full data will come from API refresh
              // or we can keep the existing assignedUser if it exists
              return {
                ...item,
                assignedUser:
                  item.assignedUser?.id === userId
                    ? item.assignedUser
                    : { id: userId, firstName: '', lastName: '', email: '' },
              };
            }
            return item;
          },
        );
      }
    });
    builder.addCase(assignPhoneNumberRequest.rejected, (state, action) => {
      state.assignPhoneNumberRequest.message = action.payload.message;
      state.assignPhoneNumberRequest.status = RequestStatus.Failed;
      state.assignPhoneNumberRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // Bulk Assign Phone Number
    builder.addCase(bulkAssignPhoneNumberRequest.pending, (state) => {
      state.bulkAssignPhoneNumberRequest.status = RequestStatus.Pending;
      state.bulkAssignPhoneNumberRequest.error = null;
      state.bulkAssignPhoneNumberRequest.message = null;
    });
    builder.addCase(bulkAssignPhoneNumberRequest.fulfilled, (state, action) => {
      state.bulkAssignPhoneNumberRequest.message = action.payload.message;
      state.bulkAssignPhoneNumberRequest.status = RequestStatus.Succeeded;
      state.bulkAssignPhoneNumberRequest.error = null;
    });
    builder.addCase(bulkAssignPhoneNumberRequest.rejected, (state, action) => {
      state.bulkAssignPhoneNumberRequest.message = action.payload.message;
      state.bulkAssignPhoneNumberRequest.status = RequestStatus.Failed;
      state.bulkAssignPhoneNumberRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // Set Phone Number Label
    builder.addCase(setPhoneNumberLabelRequest.pending, (state) => {
      state.setPhoneNumberLabelRequest.status = RequestStatus.Pending;
      state.setPhoneNumberLabelRequest.error = null;
      state.setPhoneNumberLabelRequest.message = null;
    });
    builder.addCase(setPhoneNumberLabelRequest.fulfilled, (state, action) => {
      state.setPhoneNumberLabelRequest.message = action.payload.message;
      state.setPhoneNumberLabelRequest.status = RequestStatus.Succeeded;
      state.setPhoneNumberLabelRequest.error = null;
    });
    builder.addCase(setPhoneNumberLabelRequest.rejected, (state, action) => {
      state.setPhoneNumberLabelRequest.message = action.payload.message;
      state.setPhoneNumberLabelRequest.status = RequestStatus.Failed;
      state.setPhoneNumberLabelRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // Bulk Set Phone Number Label
    builder.addCase(bulkSetPhoneNumberLabelRequest.pending, (state) => {
      state.bulkSetPhoneNumberLabelRequest.status = RequestStatus.Pending;
      state.bulkSetPhoneNumberLabelRequest.error = null;
      state.bulkSetPhoneNumberLabelRequest.message = null;
    });
    builder.addCase(bulkUpdatePhoneNumberSettingsRequest.pending, (state) => {
      state.bulkUpdatePhoneNumberSettingsRequest.status = RequestStatus.Pending;
      state.bulkUpdatePhoneNumberSettingsRequest.error = null;
      state.bulkUpdatePhoneNumberSettingsRequest.message = null;
    });
    builder.addCase(
      bulkSetPhoneNumberLabelRequest.fulfilled,
      (state, action) => {
        state.bulkSetPhoneNumberLabelRequest.message = action.payload.message;
        state.bulkSetPhoneNumberLabelRequest.status = RequestStatus.Succeeded;
        state.bulkSetPhoneNumberLabelRequest.error = null;
      },
    );
    builder.addCase(
      bulkUpdatePhoneNumberSettingsRequest.fulfilled,
      (state, action) => {
        state.bulkUpdatePhoneNumberSettingsRequest.message =
          action.payload.message;
        state.bulkUpdatePhoneNumberSettingsRequest.status =
          RequestStatus.Succeeded;
        state.bulkUpdatePhoneNumberSettingsRequest.error = null;
      },
    );
    builder.addCase(
      bulkSetPhoneNumberLabelRequest.rejected,
      (state, action) => {
        state.bulkSetPhoneNumberLabelRequest.message = action.payload.message;
        state.bulkSetPhoneNumberLabelRequest.status = RequestStatus.Failed;
        state.bulkSetPhoneNumberLabelRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
    builder.addCase(
      bulkUpdatePhoneNumberSettingsRequest.rejected,
      (state, action) => {
        state.bulkUpdatePhoneNumberSettingsRequest.message =
          action.payload.message;
        state.bulkUpdatePhoneNumberSettingsRequest.status =
          RequestStatus.Failed;
        state.bulkUpdatePhoneNumberSettingsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
    // Update Phone Number Settings
    builder.addCase(
      updatePhoneNumberSettingsRequest.pending,
      (state, action) => {
        const { settings } = action.meta.arg;
        // Track request state for each setting code in the payload
        settings.forEach(({ code }: { code: string; value: string }) => {
          if (!state.updatePhoneNumberSettingsRequest[code]) {
            state.updatePhoneNumberSettingsRequest[code] = {
              status: RequestStatus.Ideal,
              message: null,
              error: null,
            };
          }
          state.updatePhoneNumberSettingsRequest[code].status =
            RequestStatus.Pending;
          state.updatePhoneNumberSettingsRequest[code].error = null;
          state.updatePhoneNumberSettingsRequest[code].message = null;
        });
      },
    );
    builder.addCase(
      updatePhoneNumberSettingsRequest.fulfilled,
      (state, action) => {
        const { settings } = action.meta.arg;
        // Update request state for each setting code in the payload
        settings.forEach(({ code }: { code: string; value: string }) => {
          if (state.updatePhoneNumberSettingsRequest[code]) {
            state.updatePhoneNumberSettingsRequest[code].status =
              RequestStatus.Succeeded;
            state.updatePhoneNumberSettingsRequest[code].message =
              action.payload.message;
            state.updatePhoneNumberSettingsRequest[code].error = null;
          }
        });
      },
    );
    builder.addCase(
      updatePhoneNumberSettingsRequest.rejected,
      (state, action) => {
        const { settings } = action.meta.arg;
        // Update request state for each setting code in the payload
        settings.forEach(({ code }: { code: string; value: string }) => {
          if (state.updatePhoneNumberSettingsRequest[code]) {
            state.updatePhoneNumberSettingsRequest[code].status =
              RequestStatus.Failed;
            state.updatePhoneNumberSettingsRequest[code].message =
              action.payload.message;
            state.updatePhoneNumberSettingsRequest[code].error =
              !action.payload.isHandled && action.payload;
          }
        });
      },
    );

    // Set primary phone number
    builder.addCase(setPrimaryPhoneNumberRequest.pending, (state) => {
      state.setPrimaryPhoneNumberRequest.status = RequestStatus.Pending;
      state.setPrimaryPhoneNumberRequest.error = null;
      state.setPrimaryPhoneNumberRequest.message = null;
    });
    builder.addCase(setPrimaryPhoneNumberRequest.fulfilled, (state, action) => {
      state.setPrimaryPhoneNumberRequest.status = RequestStatus.Succeeded;
      state.setPrimaryPhoneNumberRequest.message = action.payload.message;
      state.setPrimaryPhoneNumberRequest.error = null;

      const { phoneNumberId } = action.meta.arg;
      if (state.myPhoneNumbersList?.phoneNumbers) {
        state.myPhoneNumbersList.phoneNumbers = state.myPhoneNumbersList.phoneNumbers.map(
          (item) => ({
            ...item,
            isPrimaryNumber: item.id === phoneNumberId,
          }),
        );
      }
    });
    builder.addCase(setPrimaryPhoneNumberRequest.rejected, (state, action) => {
      state.setPrimaryPhoneNumberRequest.status = RequestStatus.Failed;
      state.setPrimaryPhoneNumberRequest.message = action.payload.message;
      state.setPrimaryPhoneNumberRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Delete phone number
    builder.addCase(deletePhoneNumberRequest.pending, (state) => {
      state.deletePhoneNumberRequest.status = RequestStatus.Pending;
      state.deletePhoneNumberRequest.error = null;
      state.deletePhoneNumberRequest.message = null;
    });
    builder.addCase(deletePhoneNumberRequest.fulfilled, (state, action) => {
      state.deletePhoneNumberRequest.status = RequestStatus.Succeeded;
      state.deletePhoneNumberRequest.message = action.payload.message;
      state.deletePhoneNumberRequest.error = null;

      // Remove the deleted phone number from the list
      const deletedId = action.meta.arg;
      if (state.myPhoneNumbersList?.phoneNumbers) {
        state.myPhoneNumbersList.phoneNumbers = state.myPhoneNumbersList.phoneNumbers.filter(
          (item) => item.id !== deletedId,
        );
      }
    });
    builder.addCase(deletePhoneNumberRequest.rejected, (state, action) => {
      state.deletePhoneNumberRequest.status = RequestStatus.Failed;
      state.deletePhoneNumberRequest.message = action.payload.message;
      state.deletePhoneNumberRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Revoke Phone Number Release
    builder.addCase(revokePhoneNumberReleaseRequest.pending, (state) => {
      state.revokePhoneNumberReleaseRequest.status = RequestStatus.Pending;
      state.revokePhoneNumberReleaseRequest.error = null;
      state.revokePhoneNumberReleaseRequest.message = null;
    });
    builder.addCase(
      revokePhoneNumberReleaseRequest.fulfilled,
      (state, action) => {
        state.revokePhoneNumberReleaseRequest.status = RequestStatus.Succeeded;
        state.revokePhoneNumberReleaseRequest.message = action.payload.message;
        state.revokePhoneNumberReleaseRequest.error = null;
      },
    );
    builder.addCase(
      revokePhoneNumberReleaseRequest.rejected,
      (state, action) => {
        state.revokePhoneNumberReleaseRequest.status = RequestStatus.Failed;
        state.revokePhoneNumberReleaseRequest.message = action.payload.message;
        state.revokePhoneNumberReleaseRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Voicemail Template Minimal List
    builder.addCase(getVoicemailTemplateMinimalListRequest.pending, (state) => {
      state.getVoicemailTemplateMinimalListRequest.status =
        RequestStatus.Pending;
      state.getVoicemailTemplateMinimalListRequest.error = null;
      state.getVoicemailTemplateMinimalListRequest.message = null;
    });
    builder.addCase(
      getVoicemailTemplateMinimalListRequest.fulfilled,
      (state, action) => {
        state.getVoicemailTemplateMinimalListRequest.status =
          RequestStatus.Succeeded;
        state.getVoicemailTemplateMinimalListRequest.message =
          action.payload.message;
        state.getVoicemailTemplateMinimalListRequest.error = null;
        const payload = action?.payload?.payload;
        const pageNumber = action.meta.arg?.pageNumber ?? 1;
        if (
          pageNumber > 1 &&
          payload?.templates?.length &&
          state.voicemailTemplateMinimalList?.templates
        ) {
          state.voicemailTemplateMinimalList = {
            templates: [
              ...state.voicemailTemplateMinimalList.templates,
              ...payload.templates,
            ],
            total: payload.total,
          };
        } else {
          state.voicemailTemplateMinimalList = payload;
        }
      },
    );
    builder.addCase(
      getVoicemailTemplateMinimalListRequest.rejected,
      (state, action) => {
        state.getVoicemailTemplateMinimalListRequest.status =
          RequestStatus.Failed;
        state.getVoicemailTemplateMinimalListRequest.message =
          action.payload.message;
        state.getVoicemailTemplateMinimalListRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Drop Voicemail Template
    builder.addCase(dropVoicemailTemplateRequest.pending, (state) => {
      state.dropVoicemailTemplateRequest.status = RequestStatus.Pending;
      state.dropVoicemailTemplateRequest.error = null;
      state.dropVoicemailTemplateRequest.message = null;
    });
    builder.addCase(dropVoicemailTemplateRequest.fulfilled, (state, action) => {
      state.dropVoicemailTemplateRequest.status = RequestStatus.Succeeded;
      state.dropVoicemailTemplateRequest.message = action.payload.message;
      state.dropVoicemailTemplateRequest.error = null;
    });
    builder.addCase(dropVoicemailTemplateRequest.rejected, (state, action) => {
      state.dropVoicemailTemplateRequest.status = RequestStatus.Failed;
      state.dropVoicemailTemplateRequest.message = action.payload.message;
      state.dropVoicemailTemplateRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Call Recording
    builder.addCase(callRecordingRequest.pending, (state) => {
      state.callRecordingRequest.status = RequestStatus.Pending;
      state.callRecordingRequest.error = null;
      state.callRecordingRequest.message = null;
    });
    builder.addCase(callRecordingRequest.fulfilled, (state, action) => {
      state.callRecordingRequest.status = RequestStatus.Succeeded;
      state.callRecordingRequest.message = action.payload.message;
      state.callRecordingRequest.error = null;
    });
    builder.addCase(callRecordingRequest.rejected, (state, action) => {
      state.callRecordingRequest.status = RequestStatus.Failed;
      state.callRecordingRequest.message = action.payload.message;
      state.callRecordingRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // List all call logs
    builder.addCase(listAllCallLogsRequest.pending, (state) => {
      state.listAllCallLogsRequest.status = RequestStatus.Pending;
      state.listAllCallLogsRequest.error = null;
      state.listAllCallLogsRequest.message = null;
    });
    builder.addCase(listAllCallLogsRequest.fulfilled, (state, action) => {
      state.listAllCallLogsRequest.status = RequestStatus.Succeeded;
      state.listAllCallLogsRequest.message = action.payload.message;
      state.listAllCallLogsRequest.error = null;
      state.listAllCallLogsResponse = action.payload.payload;
      if (state.activeCallLogsTab === 'all' || !state.activeCallLogsTab) {
        state.activeCallLogsResponse = action.payload.payload;
      }
    });
    builder.addCase(listAllCallLogsRequest.rejected, (state, action) => {
      state.listAllCallLogsRequest.status = RequestStatus.Failed;
      state.listAllCallLogsRequest.message = action.payload.message;
      state.listAllCallLogsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // List missed calls
    builder.addCase(listMissedCallsRequest.pending, (state) => {
      state.listMissedCallsRequest.status = RequestStatus.Pending;
      state.listMissedCallsRequest.error = null;
      state.listMissedCallsRequest.message = null;
    });
    builder.addCase(listMissedCallsRequest.fulfilled, (state, action) => {
      state.listMissedCallsRequest.status = RequestStatus.Succeeded;
      state.listMissedCallsRequest.message = action.payload.message;
      state.listMissedCallsRequest.error = null;
      state.listMissedCallsResponse = action.payload.payload;
      if (state.activeCallLogsTab === 'missed') {
        state.activeCallLogsResponse = action.payload.payload;
      }
    });
    builder.addCase(listMissedCallsRequest.rejected, (state, action) => {
      state.listMissedCallsRequest.status = RequestStatus.Failed;
      state.listMissedCallsRequest.message = action.payload.message;
      state.listMissedCallsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // List incoming voicemails
    builder.addCase(listIncomingVoicemailsRequest.pending, (state) => {
      state.listIncomingVoicemailsRequest.status = RequestStatus.Pending;
      state.listIncomingVoicemailsRequest.error = null;
      state.listIncomingVoicemailsRequest.message = null;
    });
    builder.addCase(
      listIncomingVoicemailsRequest.fulfilled,
      (state, action) => {
        state.listIncomingVoicemailsRequest.status = RequestStatus.Succeeded;
        state.listIncomingVoicemailsRequest.message = action.payload.message;
        state.listIncomingVoicemailsRequest.error = null;
        state.listIncomingVoicemailsResponse = action.payload.payload;
        if (state.activeCallLogsTab === 'voicemails') {
          state.activeCallLogsResponse = action.payload.payload;
        }
      },
    );
    builder.addCase(listIncomingVoicemailsRequest.rejected, (state, action) => {
      state.listIncomingVoicemailsRequest.status = RequestStatus.Failed;
      state.listIncomingVoicemailsRequest.message = action.payload.message;
      state.listIncomingVoicemailsRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // Get Network Diagnostic Token
    builder.addCase(getNetworkDiagnosticTokenRequest.pending, (state) => {
      state.getNetworkDiagnosticTokenRequest.status = RequestStatus.Pending;
      state.getNetworkDiagnosticTokenRequest.error = null;
      state.getNetworkDiagnosticTokenRequest.message = null;
    });
    builder.addCase(
      getNetworkDiagnosticTokenRequest.fulfilled,
      (state, action) => {
        state.getNetworkDiagnosticTokenRequest.message = action.payload.message;
        state.getNetworkDiagnosticTokenRequest.status = RequestStatus.Succeeded;
        state.getNetworkDiagnosticTokenRequest.error = null;
        state.networkDiagnosticIceServers = action.payload
          .payload as RTCIceServer[];
      },
    );
    builder.addCase(
      getNetworkDiagnosticTokenRequest.rejected,
      (state, action) => {
        state.getNetworkDiagnosticTokenRequest.message = action.payload.message;
        state.getNetworkDiagnosticTokenRequest.status = RequestStatus.Failed;
        state.getNetworkDiagnosticTokenRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Delete Call Log
    builder.addCase(deleteCallLogRequest.pending, (state) => {
      state.deleteCallLogRequest.status = RequestStatus.Pending;
      state.deleteCallLogRequest.error = null;
      state.deleteCallLogRequest.message = null;
    });
    builder.addCase(deleteCallLogRequest.fulfilled, (state, action) => {
      state.deleteCallLogRequest.status = RequestStatus.Succeeded;
      state.deleteCallLogRequest.message = action.payload.message;
      state.deleteCallLogRequest.error = null;
    });
    builder.addCase(deleteCallLogRequest.rejected, (state, action) => {
      state.deleteCallLogRequest.status = RequestStatus.Failed;
      state.deleteCallLogRequest.message = action.payload.message;
      state.deleteCallLogRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Mark unseen missed call count
    builder.addCase(markUnseenMissedCallCountRequest.pending, (state) => {
      state.markUnseenMissedCallCountRequest.status = RequestStatus.Pending;
      state.markUnseenMissedCallCountRequest.error = null;
      state.markUnseenMissedCallCountRequest.message = null;
    });
    builder.addCase(
      markUnseenMissedCallCountRequest.fulfilled,
      (state, action) => {
        state.markUnseenMissedCallCountRequest.status = RequestStatus.Succeeded;
        state.markUnseenMissedCallCountRequest.message = action.payload.message;
        state.markUnseenMissedCallCountRequest.error = null;
        state.missedCallCount = action.payload.payload.count;
      },
    );
    builder.addCase(
      markUnseenMissedCallCountRequest.rejected,
      (state, action) => {
        state.markUnseenMissedCallCountRequest.status = RequestStatus.Failed;
        state.markUnseenMissedCallCountRequest.message = action.payload.message;
        state.markUnseenMissedCallCountRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Mark call as seen
    builder.addCase(markCallAsSeenRequest.pending, (state) => {
      state.markCallAsSeenRequest.status = RequestStatus.Pending;
      state.markCallAsSeenRequest.error = null;
      state.markCallAsSeenRequest.message = null;
    });
    builder.addCase(markCallAsSeenRequest.fulfilled, (state, action) => {
      state.markCallAsSeenRequest.status = RequestStatus.Ideal;
      state.markCallAsSeenRequest.message = action.payload.message;
      state.markCallAsSeenRequest.error = null;

      const callIds: number[] = action.meta.arg;

      if (state.activeCallLogsResponse) {
        const updateLog = (log: DetailedCallLog): DetailedCallLog => {
          if (callIds.length === 0) {
            const isMissed =
              log.callType === 'missed' ||
              log.callType === 'missed-with-voicemail';
            return isMissed ? { ...log, isSeen: true } : log;
          }
          return callIds.includes(log.id) && !log.isSeen
            ? { ...log, isSeen: true }
            : log;
        };

        if (state.activeCallLogsResponse.allCalls) {
          state.activeCallLogsResponse.allCalls = state.activeCallLogsResponse.allCalls.map(
            updateLog,
          );
        }
        if (state.activeCallLogsResponse.missedCalls) {
          state.activeCallLogsResponse.missedCalls = state.activeCallLogsResponse.missedCalls.map(
            updateLog,
          );
        }
        if (state.activeCallLogsResponse.incomingVoicemails) {
          state.activeCallLogsResponse.incomingVoicemails = state.activeCallLogsResponse.incomingVoicemails.map(
            updateLog,
          );
        }
      }

      if (callIds.length === 0) {
        state.missedCallCount = 0;
      } else {
        state.missedCallCount = Math.max(
          0,
          state.missedCallCount - callIds.length,
        );
      }
    });
    builder.addCase(markCallAsSeenRequest.rejected, (state, action) => {
      state.markCallAsSeenRequest.status = RequestStatus.Failed;
      state.markCallAsSeenRequest.message = action.payload.message;
      state.markCallAsSeenRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get signed URL for call recording
    builder.addCase(getSignedUrlForCallRecordingRequest.pending, (state) => {
      state.getSignedUrlForCallRecordingRequest.status = RequestStatus.Pending;
      state.getSignedUrlForCallRecordingRequest.error = null;
      state.getSignedUrlForCallRecordingRequest.message = null;
    });
    builder.addCase(
      getSignedUrlForCallRecordingRequest.fulfilled,
      (state, action) => {
        const callId = action.meta.arg;
        state.getSignedUrlForCallRecordingRequest.status =
          RequestStatus.Succeeded;
        state.getSignedUrlForCallRecordingRequest.message =
          action.payload.message;
        state.getSignedUrlForCallRecordingRequest.error = null;
        state.getSignedUrlForCallRecordingResponses[callId] =
          action.payload.payload;
        state.getSignedUrlForCallRecordingResponse = action.payload.payload;
      },
    );
    builder.addCase(
      getSignedUrlForCallRecordingRequest.rejected,
      (state, action) => {
        state.getSignedUrlForCallRecordingRequest.status = RequestStatus.Failed;
        state.getSignedUrlForCallRecordingRequest.message =
          action.payload.message;
        state.getSignedUrlForCallRecordingRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get call outcome list
    builder.addCase(getCallOutcomeListRequest.pending, (state) => {
      state.getCallOutcomeListRequest.status = RequestStatus.Pending;
      state.getCallOutcomeListRequest.error = null;
      state.getCallOutcomeListRequest.message = null;
    });
    builder.addCase(getCallOutcomeListRequest.fulfilled, (state, action) => {
      state.getCallOutcomeListRequest.status = RequestStatus.Succeeded;
      state.getCallOutcomeListRequest.message = action.payload.message;
      state.getCallOutcomeListRequest.error = null;
      state.getCallOutcomeListResponse = action.payload.payload;
    });
    builder.addCase(getCallOutcomeListRequest.rejected, (state, action) => {
      state.getCallOutcomeListRequest.status = RequestStatus.Failed;
      state.getCallOutcomeListRequest.message = action.payload.message;
      state.getCallOutcomeListRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get voicemail template list
    builder.addCase(getVoicemailTemplateListRequest.pending, (state) => {
      state.getVoicemailTemplateListRequest.status = RequestStatus.Pending;
      state.getVoicemailTemplateListRequest.error = null;
      state.getVoicemailTemplateListRequest.message = null;
    });
    builder.addCase(
      getVoicemailTemplateListRequest.fulfilled,
      (state, action) => {
        state.getVoicemailTemplateListRequest.status = RequestStatus.Succeeded;
        state.getVoicemailTemplateListRequest.message = action.payload.message;
        state.getVoicemailTemplateListRequest.error = null;
        state.getVoicemailTemplateListResponse = action.payload.payload;
      },
    );
    builder.addCase(
      getVoicemailTemplateListRequest.rejected,
      (state, action) => {
        state.getVoicemailTemplateListRequest.status = RequestStatus.Failed;
        state.getVoicemailTemplateListRequest.message = action.payload.message;
        state.getVoicemailTemplateListRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get signed URL for voicemail template
    builder.addCase(
      getSignedUrlForVoicemailTemplateRequest.pending,
      (state, action) => {
        state.getSignedUrlForVoicemailTemplateRequest.status =
          RequestStatus.Pending;
        state.getSignedUrlForVoicemailTemplateRequest.error = null;
        state.getSignedUrlForVoicemailTemplateRequest.message = null;
        state.getSignedUrlForVoicemailTemplatePendingId = action.meta.arg;
      },
    );
    builder.addCase(
      getSignedUrlForVoicemailTemplateRequest.fulfilled,
      (state, action) => {
        const templateId = action.meta.arg;
        state.getSignedUrlForVoicemailTemplateRequest.status =
          RequestStatus.Succeeded;
        state.getSignedUrlForVoicemailTemplateRequest.message =
          action.payload.message;
        state.getSignedUrlForVoicemailTemplateRequest.error = null;
        state.getSignedUrlForVoicemailTemplateResponse = action.payload.payload;
        state.getSignedUrlForVoicemailTemplateResponses[templateId] =
          action.payload.payload;
        state.getSignedUrlForVoicemailTemplatePendingId = null;
      },
    );
    builder.addCase(
      getSignedUrlForVoicemailTemplateRequest.rejected,
      (state, action) => {
        state.getSignedUrlForVoicemailTemplateRequest.status =
          RequestStatus.Failed;
        state.getSignedUrlForVoicemailTemplateRequest.message =
          action.payload.message;
        state.getSignedUrlForVoicemailTemplateRequest.error =
          !action.payload.isHandled && action.payload;
        state.getSignedUrlForVoicemailTemplatePendingId = null;
      },
    );

    // Create voicemail template
    builder.addCase(createVoicemailTemplateRequest.pending, (state) => {
      state.createVoicemailTemplateRequest.status = RequestStatus.Pending;
      state.createVoicemailTemplateRequest.error = null;
      state.createVoicemailTemplateRequest.message = null;
    });
    builder.addCase(
      createVoicemailTemplateRequest.fulfilled,
      (state, action) => {
        state.createVoicemailTemplateRequest.status = RequestStatus.Succeeded;
        state.createVoicemailTemplateRequest.message = action.payload.message;
        state.createVoicemailTemplateRequest.error = null;
      },
    );
    builder.addCase(
      createVoicemailTemplateRequest.rejected,
      (state, action) => {
        state.createVoicemailTemplateRequest.status = RequestStatus.Failed;
        state.createVoicemailTemplateRequest.message = action.payload.message;
        state.createVoicemailTemplateRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Update voicemail template
    builder.addCase(updateVoicemailTemplateRequest.pending, (state) => {
      state.updateVoicemailTemplateRequest.status = RequestStatus.Pending;
      state.updateVoicemailTemplateRequest.error = null;
      state.updateVoicemailTemplateRequest.message = null;
    });
    builder.addCase(
      updateVoicemailTemplateRequest.fulfilled,
      (state, action) => {
        state.updateVoicemailTemplateRequest.status = RequestStatus.Succeeded;
        state.updateVoicemailTemplateRequest.message = action.payload.message;
        state.updateVoicemailTemplateRequest.error = null;
      },
    );
    builder.addCase(
      updateVoicemailTemplateRequest.rejected,
      (state, action) => {
        state.updateVoicemailTemplateRequest.status = RequestStatus.Failed;
        state.updateVoicemailTemplateRequest.message = action.payload.message;
        state.updateVoicemailTemplateRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Delete voicemail template
    builder.addCase(deleteVoicemailTemplateRequest.pending, (state) => {
      state.deleteVoicemailTemplateRequest.status = RequestStatus.Pending;
      state.deleteVoicemailTemplateRequest.error = null;
      state.deleteVoicemailTemplateRequest.message = null;
    });
    builder.addCase(
      deleteVoicemailTemplateRequest.fulfilled,
      (state, action) => {
        state.deleteVoicemailTemplateRequest.status = RequestStatus.Succeeded;
        state.deleteVoicemailTemplateRequest.message = action.payload.message;
        state.deleteVoicemailTemplateRequest.error = null;
      },
    );
    builder.addCase(
      deleteVoicemailTemplateRequest.rejected,
      (state, action) => {
        state.deleteVoicemailTemplateRequest.status = RequestStatus.Failed;
        state.deleteVoicemailTemplateRequest.message = action.payload.message;
        state.deleteVoicemailTemplateRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get pre-signed URL
    builder.addCase(getPreSignedUrlRequest.pending, (state) => {
      state.getPreSignedUrlRequest.status = RequestStatus.Pending;
      state.getPreSignedUrlRequest.error = null;
      state.getPreSignedUrlRequest.message = null;
    });
    builder.addCase(getPreSignedUrlRequest.fulfilled, (state, action) => {
      state.getPreSignedUrlRequest.status = RequestStatus.Succeeded;
      state.getPreSignedUrlRequest.message = action.payload.message;
      state.getPreSignedUrlResponse = action.payload.payload;
    });
    builder.addCase(getPreSignedUrlRequest.rejected, (state, action) => {
      state.getPreSignedUrlRequest.status = RequestStatus.Failed;
      state.getPreSignedUrlRequest.message = action.payload.message;
      state.getPreSignedUrlRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Call logs list counts
    builder.addCase(callLogsListCountsRequest.pending, (state) => {
      state.callLogsListCountsRequest.status = RequestStatus.Pending;
      state.callLogsListCountsRequest.error = null;
      state.callLogsListCountsRequest.message = null;
    });
    builder.addCase(callLogsListCountsRequest.fulfilled, (state, action) => {
      state.callLogsListCountsRequest.status = RequestStatus.Succeeded;
      state.callLogsListCountsRequest.message = action.payload.message;
      state.callLogsListCountsResponse = action.payload.payload;
    });
    builder.addCase(callLogsListCountsRequest.rejected, (state, action) => {
      state.callLogsListCountsRequest.status = RequestStatus.Failed;
      state.callLogsListCountsRequest.message = action.payload.message;
      state.callLogsListCountsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Assign call outcome
    builder.addCase(assignCallOutcomeRequest.pending, (state) => {
      state.assignCallOutcomeRequest.status = RequestStatus.Pending;
      state.assignCallOutcomeRequest.error = null;
      state.assignCallOutcomeRequest.message = null;
    });
    builder.addCase(assignCallOutcomeRequest.fulfilled, (state, action) => {
      state.assignCallOutcomeRequest.status = RequestStatus.Succeeded;
      state.assignCallOutcomeRequest.message = action.payload.message;
      state.assignCallOutcomeRequest.error = null;
      const { outcomeId, dialerCallLogId } = action.meta.arg;
      const outcomeList = state.getCallOutcomeListResponse ?? [];
      const newOutcome = outcomeList.find((o) => o?.id === outcomeId);
      if (!newOutcome) return;

      const updatedCallOutcome = {
        id: newOutcome.id,
        name: newOutcome.name,
        bgColor: newOutcome.bgColor,
        textColor: newOutcome.textColor,
        sentiment: newOutcome.sentiment,
        isDefault: newOutcome.isDefault,
      };

      const updateLog = (log) =>
        log?.id === dialerCallLogId
          ? { ...log, callOutcome: updatedCallOutcome }
          : log;

      const updateResponse = (response: CallLogsResponse | null) => {
        if (!response) return response;
        return {
          ...response,
          allCalls: response.allCalls?.map(updateLog),
          missedCalls: response.missedCalls?.map(updateLog),
          incomingVoicemails: response.incomingVoicemails?.map(updateLog),
        };
      };

      state.listAllCallLogsResponse = updateResponse(
        state.listAllCallLogsResponse,
      );
      state.listMissedCallsResponse = updateResponse(
        state.listMissedCallsResponse,
      );
      state.listIncomingVoicemailsResponse = updateResponse(
        state.listIncomingVoicemailsResponse,
      );

      if (state.activeCallLogsTab === 'missed') {
        state.activeCallLogsResponse = state.listMissedCallsResponse;
      } else if (state.activeCallLogsTab === 'voicemails') {
        state.activeCallLogsResponse = state.listIncomingVoicemailsResponse;
      } else {
        state.activeCallLogsResponse = state.listAllCallLogsResponse;
      }
    });
    builder.addCase(assignCallOutcomeRequest.rejected, (state, action) => {
      state.assignCallOutcomeRequest.status = RequestStatus.Failed;
      state.assignCallOutcomeRequest.message = action.payload.message;
      state.assignCallOutcomeRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Reports Stats
    builder.addCase(getReportsStatsRequest.pending, (state) => {
      state.getReportsStatsRequest.status = RequestStatus.Pending;
      state.getReportsStatsRequest.error = null;
      state.getReportsStatsRequest.message = null;
    });
    builder.addCase(getReportsStatsRequest.fulfilled, (state, action) => {
      state.getReportsStatsRequest.status = RequestStatus.Succeeded;
      state.getReportsStatsRequest.message = action.payload.message;
      state.getReportsStatsResponse = action.payload.payload;
    });
    builder.addCase(getReportsStatsRequest.rejected, (state, action) => {
      state.getReportsStatsRequest.status = RequestStatus.Failed;
      state.getReportsStatsRequest.message = action.payload.message;
      state.getReportsStatsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Report Individual List
    builder.addCase(getReportIndividualListRequest.pending, (state) => {
      state.getReportIndividualListRequest.status = RequestStatus.Pending;
      state.getReportIndividualListRequest.error = null;
      state.getReportIndividualListRequest.message = null;
    });
    builder.addCase(
      getReportIndividualListRequest.fulfilled,
      (state, action) => {
        state.getReportIndividualListRequest.status = RequestStatus.Succeeded;
        state.getReportIndividualListRequest.message = action.payload.message;
        state.getReportIndividualListResponse = action.payload.payload;
      },
    );
    builder.addCase(
      getReportIndividualListRequest.rejected,
      (state, action) => {
        state.getReportIndividualListRequest.status = RequestStatus.Failed;
        state.getReportIndividualListRequest.message = action.payload.message;
        state.getReportIndividualListRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Report Team List
    builder.addCase(getReportTeamListRequest.pending, (state) => {
      state.getReportTeamListRequest.status = RequestStatus.Pending;
      state.getReportTeamListRequest.error = null;
      state.getReportTeamListRequest.message = null;
    });
    builder.addCase(getReportTeamListRequest.fulfilled, (state, action) => {
      state.getReportTeamListRequest.status = RequestStatus.Succeeded;
      state.getReportTeamListRequest.message = action.payload.message;
      state.getReportTeamListResponse = action.payload.payload;
    });
    builder.addCase(getReportTeamListRequest.rejected, (state, action) => {
      state.getReportTeamListRequest.status = RequestStatus.Failed;
      state.getReportTeamListRequest.message = action.payload.message;
      state.getReportTeamListRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Export Call Reports
    builder.addCase(exportCallReportsRequest.pending, (state) => {
      state.exportCallReportsRequest.status = RequestStatus.Pending;
      state.exportCallReportsRequest.error = null;
      state.exportCallReportsRequest.message = null;
    });
    builder.addCase(exportCallReportsRequest.fulfilled, (state, action) => {
      state.exportCallReportsRequest.status = RequestStatus.Succeeded;
      state.exportCallReportsRequest.message = action.payload.message;
      state.exportCallReportsResponse = action.payload.payload;
    });
    builder.addCase(exportCallReportsRequest.rejected, (state, action) => {
      state.exportCallReportsRequest.status = RequestStatus.Failed;
      state.exportCallReportsRequest.message = action.payload.message;
      state.exportCallReportsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Export Call Logs Detailed
    builder.addCase(exportCallLogsDetailedRequest.pending, (state) => {
      state.exportCallLogsDetailedRequest.status = RequestStatus.Pending;
      state.exportCallLogsDetailedRequest.error = null;
      state.exportCallLogsDetailedRequest.message = null;
    });
    builder.addCase(
      exportCallLogsDetailedRequest.fulfilled,
      (state, action) => {
        state.exportCallLogsDetailedRequest.status = RequestStatus.Succeeded;
        state.exportCallLogsDetailedRequest.message = action.payload.message;
        state.exportCallLogsDetailedResponse = action.payload.payload;
      },
    );
    builder.addCase(exportCallLogsDetailedRequest.rejected, (state, action) => {
      state.exportCallLogsDetailedRequest.status = RequestStatus.Failed;
      state.exportCallLogsDetailedRequest.message = action.payload.message;
      state.exportCallLogsDetailedRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get call logs prospect drawer header
    builder.addCase(getCallLogsProspectDrawerHeaderRequest.pending, (state) => {
      state.getCallLogsProspectDrawerHeaderRequest.status =
        RequestStatus.Pending;
      state.getCallLogsProspectDrawerHeaderRequest.error = null;
      state.getCallLogsProspectDrawerHeaderRequest.message = null;
      /* Avoid merging a stale list when a new prospect/date/page request starts */
      state.getCallLogsProspectDrawerHeaderResponse = null;
    });
    builder.addCase(
      getCallLogsProspectDrawerHeaderRequest.fulfilled,
      (state, action) => {
        state.getCallLogsProspectDrawerHeaderRequest.status =
          RequestStatus.Succeeded;
        state.getCallLogsProspectDrawerHeaderRequest.message =
          action.payload.message;
        state.getCallLogsProspectDrawerHeaderResponse = action.payload.payload;
      },
    );
    builder.addCase(
      getCallLogsProspectDrawerHeaderRequest.rejected,
      (state, action) => {
        state.getCallLogsProspectDrawerHeaderRequest.status =
          RequestStatus.Failed;
        state.getCallLogsProspectDrawerHeaderRequest.message =
          action.payload.message;
        state.getCallLogsProspectDrawerHeaderRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get call logs prospect profile section
    builder.addCase(
      getCallLogsProspectProfileSectionRequest.pending,
      (state) => {
        state.getCallLogsProspectProfileSectionRequest.status =
          RequestStatus.Pending;
        state.getCallLogsProspectProfileSectionRequest.error = null;
        state.getCallLogsProspectProfileSectionRequest.message = null;
      },
    );
    builder.addCase(
      getCallLogsProspectProfileSectionRequest.fulfilled,
      (state, action) => {
        state.getCallLogsProspectProfileSectionRequest.status =
          RequestStatus.Succeeded;
        state.getCallLogsProspectProfileSectionRequest.message =
          action.payload.message;
        state.getCallLogsProspectProfileSectionResponse =
          action.payload.payload;
      },
    );
    builder.addCase(
      getCallLogsProspectProfileSectionRequest.rejected,
      (state, action) => {
        state.getCallLogsProspectProfileSectionRequest.status =
          RequestStatus.Failed;
        state.getCallLogsProspectProfileSectionRequest.message =
          action.payload.message;
        state.getCallLogsProspectProfileSectionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Create call logs note
    builder.addCase(createCallLogsNoteRequest.pending, (state) => {
      state.createCallLogsNoteRequest.status = RequestStatus.Pending;
      state.createCallLogsNoteRequest.error = null;
      state.createCallLogsNoteRequest.message = null;
    });
    builder.addCase(
      createCallLogsNoteRequest.fulfilled,
      (state, { payload, meta: { arg } }) => {
        state.createCallLogsNoteRequest.status = RequestStatus.Succeeded;
        state.createCallLogsNoteRequest.message = payload.message;
        state.createCallLogsNoteResponse = payload.payload;

        const profile = state.getCallLogsProspectProfileSectionResponse as
          | Record<string, unknown>
          | null
          | undefined;

        if (
          profile &&
          callLogsProfileMatchesSavedNoteCall(profile, arg.callId)
        ) {
          state.getCallLogsProspectProfileSectionResponse = mergeCallLogsProfileSectionNote(
            profile,
            arg,
            payload.payload,
          ) as typeof state.getCallLogsProspectProfileSectionResponse;
        }
      },
    );
    builder.addCase(createCallLogsNoteRequest.rejected, (state, action) => {
      state.createCallLogsNoteRequest.status = RequestStatus.Failed;
      state.createCallLogsNoteRequest.message = action.payload.message;
      state.createCallLogsNoteRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Update call logs note
    builder.addCase(updateCallLogsNoteRequest.pending, (state) => {
      state.updateCallLogsNoteRequest.status = RequestStatus.Pending;
      state.updateCallLogsNoteRequest.error = null;
      state.updateCallLogsNoteRequest.message = null;
    });
    builder.addCase(
      updateCallLogsNoteRequest.fulfilled,
      (state, { payload, meta: { arg } }) => {
        state.updateCallLogsNoteRequest.status = RequestStatus.Succeeded;
        state.updateCallLogsNoteRequest.message = payload.message;
        state.updateCallLogsNoteResponse = payload.payload;

        const profile = state.getCallLogsProspectProfileSectionResponse as
          | Record<string, unknown>
          | null
          | undefined;

        if (
          profile &&
          callLogsProfileMatchesSavedNoteCall(profile, arg.callId)
        ) {
          state.getCallLogsProspectProfileSectionResponse = mergeCallLogsProfileSectionNote(
            profile,
            arg,
            payload.payload,
          ) as typeof state.getCallLogsProspectProfileSectionResponse;
        }
      },
    );
    builder.addCase(updateCallLogsNoteRequest.rejected, (state, action) => {
      state.updateCallLogsNoteRequest.status = RequestStatus.Failed;
      state.updateCallLogsNoteRequest.message = action.payload.message;
      state.updateCallLogsNoteRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get phone call mentionable users
    builder.addCase(getPhoneCallMentionableUsersRequest.pending, (state) => {
      state.getPhoneCallMentionableUsersRequest.status = RequestStatus.Pending;
      state.getPhoneCallMentionableUsersRequest.error = null;
      state.getPhoneCallMentionableUsersRequest.message = null;
    });
    builder.addCase(
      getPhoneCallMentionableUsersRequest.fulfilled,
      (state, action) => {
        state.getPhoneCallMentionableUsersRequest.status =
          RequestStatus.Succeeded;
        state.getPhoneCallMentionableUsersRequest.message =
          action.payload.message;
        state.getPhoneCallMentionableUsersResponse = action.payload.payload;
      },
    );
    builder.addCase(
      getPhoneCallMentionableUsersRequest.rejected,
      (state, action) => {
        state.getPhoneCallMentionableUsersRequest.status = RequestStatus.Failed;
        state.getPhoneCallMentionableUsersRequest.message =
          action.payload.message;
        state.getPhoneCallMentionableUsersRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
    // Attach prospect to call
    builder.addCase(attachProspectToCallRequest.pending, (state) => {
      state.attachProspectToCallRequest.status = RequestStatus.Pending;
      state.attachProspectToCallRequest.error = null;
      state.attachProspectToCallRequest.message = null;
    });
    builder.addCase(attachProspectToCallRequest.fulfilled, (state, action) => {
      state.attachProspectToCallRequest.message = action.payload.message;
      state.attachProspectToCallRequest.status = RequestStatus.Succeeded;
      state.attachProspectToCallRequest.error = null;
    });
    builder.addCase(attachProspectToCallRequest.rejected, (state, action) => {
      state.attachProspectToCallRequest.message = action.payload.message;
      state.attachProspectToCallRequest.status = RequestStatus.Failed;
      state.attachProspectToCallRequest.error =
        !action.payload.isHandled && action.payload;
    });
  },
});

export const {
  addPhoneNumberToCart,
  removePhoneNumberFromCart,
  resetPhoneNumbersCart,
  updateHasErrorsInContactDetailsForm,
  updateCartUniqueConfiguration,
  setIsRegionChangedInLastSearch,
  setLastSearchRegion,
  resetAddPhoneNumbersToCartRequest,
  resetRemovePhoneNumberFromCartRequest,
  resetPurchasePhoneNumbersCartRequest,
  resetGetContactsListForManualCallDialingRequest,
  resetCreateTwillioVoiceTokenRequest,
  resetInitiateCallRequest,
  resetEndInitiatedCallRequest,
  setMyPhoneNumbersListFilters,
  resetBulkSetPhoneNumberLabelRequest,
  resetSetPhoneNumberLabelRequest,
  resetAssignPhoneNumberRequest,
  resetBulkAssignPhoneNumberRequest,
  resetBulkUpdatePhoneNumberSettingsRequest,
  resetUpdatePhoneNumberSettingsRequest,
  resetSetPrimaryPhoneNumberRequest,
  resetDeletePhoneNumberRequest,
  resetRevokePhoneNumberReleaseRequest,
  setShowDialer,
  setShowIncomingCall,
  resetProspectMinimalListResponse,
  resetDialerPhoneNumberList,
  resetGetVoicemailTemplateMinimalListRequest,
  resetDropVoicemailTemplateRequest,
  resetCallRecordingRequest,
  setDialerControlsState,
  toggleDialerControl,
  resetDialerControlsState,
  setDuringCallNote,
  clearDuringCallNote,
  setInitiatedCallId,
  clearInitiatedCallId,
  setShowMicrophoneAccessWarningModal,
  resetGetNetworkDiagnosticTokenRequest,
  resetNetworkDiagnosticIceServers,
  setSelectedCallbackProspect,
  resetSelectedCallbackProspect,
  setCurrentCallManagementState,
  setActiveCallLogsTab,
  resetDeleteCallLogRequest,
  resetCreateVoicemailTemplateRequest,
  resetGetSignedUrlForVoicemailTemplateRequest,
  setCallLogsFilters,
  resetCallLogsFilters,
  resetUpdateVoicemailTemplateRequest,
  resetGetSignedUrlForVoicemailTemplateResponses,
  resetAssignCallOutcomeRequest,
  resetDeleteVoicemailTemplateRequest,
  resetMarkUnseenMissedCallCountRequest,
  resetMarkCallAsSeenRequest,
  resetGetReportTeamListRequest,
  resetGetReportIndividualListRequest,
  resetGetReportsStatsRequest,
  resetExportCallReportsRequest,
  resetExportCallLogsDetailedRequest,
  enableCallRecording,
  resetGetCallLogsProspectDrawerHeaderRequest,
  resetGetCallLogsProspectProfileSectionRequest,
  resetGetPhoneNumberDetailsRequest,
  setSingleCallDetails,
  resetCreateCallLogsNoteRequest,
  resetUpdateCallLogsNoteRequest,
  resetAttachProspectToCallRequest,
  setInitiatedCallFromTask,
  setCallNetworkLevel,
  setOngoingCallInfo,
  resetOngoingCallInfo,
} = dialerSlice.actions;

export default dialerSlice.reducer;
